import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BRAND_LOGOMARK_TRANSPARENT_PATH } from '../lib/brand-assets';
import { resolveRouterPath } from '../lib/router-path';

/**
 * Logomark + “Yes We Can” (no LLC). Logomark **height** matches the text block height
 * (`line-count × font-size × line-height`).
 *
 * - `layout="stacked"`: Yes / We Can (two lines) — default for header & footer.
 * - `layout="triple"`: Yes / We / Can (three lines).
 * - `layout="inline"`: one line.
 */
@customElement('site-brand-lockup')
export class SiteBrandLockup extends LitElement {
  @property({ type: String }) layout: 'inline' | 'stacked' | 'triple' = 'stacked';

  /** `sm` footer / PWA chrome, `md` top nav, `lg` larger moments (all scaled vs earlier defaults). */
  @property({ type: String }) size: 'sm' | 'md' | 'lg' = 'md';

  @property({ type: Boolean }) link = true;

  @property({ type: String }) tone: 'default' | 'onBlue' = 'default';

  static styles = css`
    :host {
      display: block;
      /* unitless line-height multiplier for mark height = lines × em × lh */
      --lock-lh: 1.12;
    }

    /* ~65% of prior sizes: mark + text scale together (mark height = lines × em × lh). */
    :host([size='sm']) {
      --lock-fs: 0.53rem;
    }
    :host([size='md']) {
      --lock-fs: 0.61rem;
    }
    :host([size='lg']) {
      --lock-fs: 0.69rem;
    }

    :host([layout='triple']) {
      --line-count: 3;
    }
    :host([layout='stacked']) {
      --line-count: 2;
    }
    :host([layout='inline']) {
      --line-count: 1;
    }

    :host([tone='onBlue']) {
      --brand-lock-color: #ffffff;
      --brand-lock-hover: var(--brand-accent-soft, #fdc06a);
    }

    :host(:not([tone='onBlue'])) {
      --brand-lock-color: var(--text);
      --brand-lock-hover: var(--brand-accent-soft, #fdc06a);
    }

    .brand-lock {
      display: inline-flex;
      align-items: center;
      gap: 0.42rem;
      text-decoration: none;
      color: inherit;
      max-width: 100%;
      font-size: var(--lock-fs);
      line-height: var(--lock-lh);
    }

    .brand-lock:focus-visible {
      outline: 2px solid var(--focus-visible, #4a8bc8);
      outline-offset: 3px;
      border-radius: var(--radius-sm, 4px);
    }

    .brand-lock:hover .brand-text-inner {
      color: var(--brand-lock-hover);
    }

    /* Same vertical extent as the text column: line-count × em × lh */
    .mark {
      flex-shrink: 0;
      width: auto;
      height: calc(var(--line-count) * 1em * var(--lock-lh));
      font-size: inherit;
      object-fit: contain;
      display: block;
    }

    .brand-text-inner {
      color: var(--brand-lock-color);
      font-weight: 800;
      letter-spacing: -0.03em;
      transition: color 0.15s ease;
      font-size: 1em;
      line-height: var(--lock-lh);
    }

    .triple,
    .stacked {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .triple .line,
    .stacked .line {
      display: block;
      line-height: var(--lock-lh);
    }

    .inline .t-inline {
      white-space: nowrap;
      line-height: var(--lock-lh);
    }
  `;

  render() {
    const home = resolveRouterPath();
    let text;
    if (this.layout === 'triple') {
      text = html`<span class="brand-text-inner triple">
        <span class="line">Yes</span>
        <span class="line">We</span>
        <span class="line">Can</span>
      </span>`;
    } else if (this.layout === 'stacked') {
      text = html`<span class="brand-text-inner stacked">
        <span class="line line1">Yes</span>
        <span class="line line2">We Can</span>
      </span>`;
    } else {
      text = html`<span class="brand-text-inner inline"><span class="t-inline">Yes We Can</span></span>`;
    }

    const inner = html`
      <img
        class="mark"
        src="${BRAND_LOGOMARK_TRANSPARENT_PATH}"
        width="450"
        height="265"
        alt=""
        decoding="async"
        loading="eager"
      />
      ${text}
    `;

    if (this.link) {
      return html`
        <a class="brand-lock" href="${home}" aria-label="Yes We Can LLC — home">${inner}</a>
      `;
    }
    return html`<div class="brand-lock">${inner}</div>`;
  }
}
